import { LOADING_LABEL } from "@/constants/uiLabels";

/**
 * Aufnahmegründe in der Fallbearbeitung.
 *
 * Eine bestätigte leere Zuordnung (`[]`) ist „keine Auswahl“.
 * `null` ist unbekannt (Query lädt noch, ist fehlgeschlagen oder hat kein Array
 * geliefert) und darf nicht zu `[]` werden. Es gibt keinen Katalog-Default
 * (auch nicht code „other“ / „Sonstiger Grund“).
 * Beim Speichern gehen Gründe nur mit, wenn die Bearbeitung sie geändert hat
 * und die gespeicherte Zuordnung bekannt ist.
 * Sonst bleibt der gespeicherte Stand (RPC: p_replace_intake_reasons bleibt false).
 */

const EMPTY_INTAKE_REASON_IDS: readonly string[] = [];

export const INTAKE_REASONS_LOADING_LABEL = LOADING_LABEL;
export const INTAKE_REASONS_ERROR_LABEL =
  "Aufnahmegründe konnten nicht geladen werden.";
export const INTAKE_REASONS_EMPTY_LABEL = "Keine";

export const INTAKE_REASONS_UNLOADED_SAVE_MESSAGE =
  "Die Aufnahmegründe sind noch nicht geladen. Bitte warten und erneut speichern.";
export const INTAKE_REASONS_FAILED_SAVE_MESSAGE =
  "Aufnahmegründe konnten nicht geladen werden. Bitte erneut versuchen, bevor die Gründe geändert werden.";

export type IntakeReasonsStatus = "loading" | "error" | "ready";

export function normalizeIntakeReasonIds(
  ids: readonly string[] | null | undefined,
): string[] {
  if (ids == null || ids.length === 0) return [];
  return [...ids];
}

/**
 * Stabile Referenz für die Query-Schicht.
 * `null` bleibt `null` (nicht als leeres Array maskiert).
 * Nur ein erfolgreiches leeres Array ist eine bestätigte leere Auswahl.
 */
export function intakeReasonIdsFromQuery(
  data: readonly string[] | null | undefined,
  isSuccess: boolean,
): readonly string[] | null {
  if (!isSuccess || data == null) return null;
  if (data.length === 0) return EMPTY_INTAKE_REASON_IDS;
  return data;
}

export function intakeReasonsStatusFromQuery(
  isSuccess: boolean,
  isError: boolean,
  data: readonly string[] | null | undefined,
): IntakeReasonsStatus {
  if (isError) return "error";
  if (!isSuccess || data == null) return "loading";
  return "ready";
}

export function intakeReasonViewValue(
  status: IntakeReasonsStatus,
  labels: readonly string[],
): string {
  if (status === "error") return INTAKE_REASONS_ERROR_LABEL;
  if (status === "loading") return INTAKE_REASONS_LOADING_LABEL;
  return labels.join(", ") || INTAKE_REASONS_EMPTY_LABEL;
}

/** Übersicht: Katalog noch unterwegs ist Laden, nicht die leere Auswahl „Keine“. */
export function intakeReasonOverviewValue(
  status: IntakeReasonsStatus,
  labels: readonly string[],
  catalogLoading: boolean,
): string {
  if (status !== "error" && (status === "loading" || catalogLoading)) {
    return LOADING_LABEL;
  }
  return intakeReasonViewValue(status, labels);
}

/**
 * Änderung bei unbekannter Zuordnung nicht still verwerfen.
 * Ohne Änderung bleibt der Aufruf frei, andere Felder zu speichern.
 */
export function intakeReasonsChangeBlockedMessage(
  storedIds: readonly string[] | null,
  changed: boolean,
  status: IntakeReasonsStatus,
): string | null {
  if (!changed || storedIds != null) return null;
  if (status === "error") return INTAKE_REASONS_FAILED_SAVE_MESSAGE;
  return INTAKE_REASONS_UNLOADED_SAVE_MESSAGE;
}

export function sameIntakeReasonIds(
  a: readonly string[] | null | undefined,
  b: readonly string[] | null | undefined,
): boolean {
  const left = normalizeIntakeReasonIds(a);
  const right = normalizeIntakeReasonIds(b);
  if (left.length !== right.length) return false;
  const set = new Set(left);
  return right.every((id) => set.has(id));
}

export function intakeReasonLabels(
  catalog: readonly { id: string; label: string }[],
  ids: readonly string[] | null | undefined,
): string[] {
  const selected = new Set(normalizeIntakeReasonIds(ids));
  return catalog.filter((item) => selected.has(item.id)).map((item) => item.label);
}

export type IntakeReasonUpdate =
  | { blocked: true; message: string }
  | {
      intakeReasonIds?: string[];
      replaceIntakeReasons?: boolean;
      intakeReasonNote?: string;
    };

/**
 * Payload-Anteil für update_animal_case_master.
 * `storedIds === null` und `changed`: explizit blockiert, nicht `{}`.
 * `changed === false`: Auswahl nicht angefasst, nichts ersetzen (auch wenn der
 * Formularzustand abweicht).
 * Geänderte Notiz geht getrennt mit; leerer Text löscht (NULLIF im RPC).
 */
export function buildIntakeReasonUpdate(input: {
  storedIds: readonly string[] | null;
  selectedIds: readonly string[];
  changed: boolean;
  status?: IntakeReasonsStatus;
  storedNote: string | null | undefined;
  note: string;
}): IntakeReasonUpdate {
  if (input.storedIds == null && input.changed) {
    return {
      blocked: true,
      message:
        intakeReasonsChangeBlockedMessage(
          null,
          true,
          input.status ?? "loading",
        ) ?? INTAKE_REASONS_UNLOADED_SAVE_MESSAGE,
    };
  }

  const patch: {
    intakeReasonIds?: string[];
    replaceIntakeReasons?: boolean;
    intakeReasonNote?: string;
  } = {};

  if (input.storedIds != null && input.changed) {
    const next = normalizeIntakeReasonIds(input.selectedIds);
    if (!sameIntakeReasonIds(next, input.storedIds)) {
      patch.intakeReasonIds = next;
      patch.replaceIntakeReasons = true;
    }
  }

  const nextNote = input.note.trim();
  const prevNote = (input.storedNote ?? "").trim();
  if (nextNote !== prevNote) {
    patch.intakeReasonNote = nextNote;
  }

  return patch;
}
